import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  notification,
  Modal,
  Button,
  Input,
  Select,
  Checkbox,
  Icon,
  Spin,
  message,
} from "../../../common/UIComponents";
import { TenderIcon } from "../../../common/CustomIcon";
import { OrgContext } from "../../../context/OrgContext";
import {
  fetchAgentTenderPreview,
  saveAgentTender,
  discardAgentTenderDraft,
  fetchOrderTenderSummary,
  searchTenderOrders,
} from "../../../api/agentTenders";
import { renderAlertMessage } from "../../../helpers/common";
import I18n from "../../../common/I18n";
import TypeOfOrder from "../TypeOfOrder";
import TenderEmailModal from "./TenderEmailModal";
import DeleteIcon from "../../common/DeleteIcon";
import "../../../../stylesheets/linchpin/tenders.css";

// Load Tendering phase 1 — Figma node 7015:11155; structure and CSS follow
// the prototype's modalTender/renderOrderTenderCard/renderBulkPhaseToggle.

const LEG_TYPE_ORDER = [
  "PICKUP",
  "RECOVERY",
  "INBOUND_DOCK",
  "LINEHAUL",
  "OUTBOUND_DOCK",
  "TRANSFER",
  "DELIVERY",
];
const LEG_TYPE_LABELS = {
  PICKUP: "Pickup",
  RECOVERY: "Recovery",
  INBOUND_DOCK: "Inbound Dock",
  LINEHAUL: "Linehaul",
  OUTBOUND_DOCK: "Outbound Dock",
  TRANSFER: "Transfer",
  DELIVERY: "Delivery",
};
const LEG_SHORT = {
  PICKUP: "P",
  RECOVERY: "R",
  INBOUND_DOCK: "ID",
  LINEHAUL: "L",
  OUTBOUND_DOCK: "OD",
  TRANSFER: "T",
  DELIVERY: "D",
};

const whCode = (ref) => (ref && (ref.warehouse_code || ref.code)) || "";
const legHop = (leg) => `${whCode(leg.origin)}-${whCode(leg.destination)}`;
// Figma leg-code labels (node 7015:11155): pickup "72712-ORD" (shipper zip →
// warehouse), delivery "LAX-90045" (warehouse → consignee zip), linehaul hop
// "ORD-LAX". Fall back to the leg-type name when the zip is unknown.
const legLabel = (leg, order) => {
  if (leg.leg_type === "LINEHAUL") return legHop(leg);
  if (leg.leg_type === "DELIVERY" || leg.leg_type === "OUTBOUND_DOCK" || leg.leg_type === "TRANSFER") {
    const zip = order && order.delivery_zipcode;
    return zip ? `${whCode(leg.origin)}-${zip}` : `${LEG_TYPE_LABELS[leg.leg_type]} · ${whCode(leg.origin)}`;
  }
  const zip = order && order.pickup_zipcode;
  return zip ? `${zip}-${whCode(leg.origin)}` : `${LEG_TYPE_LABELS[leg.leg_type]} · ${whCode(leg.origin)}`;
};

const eligibleLegs = (order) => order.legs.filter((leg) => !leg.already_tendered);
// NON_AGENT_WAREHOUSE orders render as normal cards with empty agent
// dropdowns; fully-TENDERED orders render as cards with per-leg
// 'Already tendered … Revoke Tender' rows. Skipped remains only for orders
// with no legs at all ("nothing left").
const orderIsSkipped = (order) => (order.legs || []).length === 0;

const triState = (legs, selection) => {
  if (!legs.length) return "none";
  const checked = legs.filter((leg) => selection[leg.__key] && selection[leg.__key].checked).length;
  if (checked === 0) return "none";
  return checked === legs.length ? "all" : "some";
};

const TenderOrdersModal = ({ visible, onClose, selectedOrders, onTendered, preselectLegTypes }) => {
  const { currentOrg } = useContext(OrgContext);
  const orgId = currentOrg.id;

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  // selection keyed by `${order_id}|${leg_id}` → { checked, agentId }
  const [selection, setSelection] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expandedLinehauls, setExpandedLinehauls] = useState({});
  const [editingLeg, setEditingLeg] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  // Orders picked from search dropdown, staged as chips until "Add to Tenders"
  const [pendingAdds, setPendingAdds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // { mode: 'tender'|'revoke', batchId, agentEmails, revokeOrders }
  const [emailStage, setEmailStage] = useState(null);
  const ordersRef = useRef(orders);
  const pendingAddsRef = useRef(pendingAdds);
  ordersRef.current = orders;
  pendingAddsRef.current = pendingAdds;

  const loadPreview = (orderIds, { append = false } = {}) => {
    const ids = (orderIds || []).filter((id) => id != null && String(id).trim() !== "");
    if (!ids.length) {
      if (!append) {
        setLoading(false);
        setOrders([]);
        setSelection({});
        setExpandedOrders({});
        setExpandedLinehauls({});
        setAvailableAgents([]);
      }
      return;
    }
    setLoading(true);
    fetchAgentTenderPreview(orgId, ids).then((result) => {
      setLoading(false);
      if (!result.success) {
        renderAlertMessage(result.errors);
        return;
      }
      const fetched = (result.orders || []).map((order) => ({
        ...order,
        legs: (order.legs || []).map((leg) => ({
          ...leg,
          __key: `${order.order_id}|${leg.leg_id}`,
        })),
      }));
      setAvailableAgents(result.available_agents || []);
      setOrders((prev) => (append ? [...prev, ...fetched.filter((o) => !prev.some((p) => p.order_id === o.order_id))] : fetched));
      const expandForPreselect =
        !append && Array.isArray(preselectLegTypes) && preselectLegTypes.length > 0;
      setExpandedOrders((prev) => {
        const next = { ...prev };
        fetched.forEach((order) => {
          if (expandForPreselect || (eligibleLegs(order).length === 0 && (order.legs || []).length > 0)) {
            next[order.order_id] = true;
          }
        });
        return next;
      });
      setSelection((prev) => {
        const next = append ? { ...prev } : {};
        const restrictTypes =
          Array.isArray(preselectLegTypes) && preselectLegTypes.length > 0
            ? preselectLegTypes.map((type) => (type || "").toString().toUpperCase())
            : null;
        fetched.forEach((order) => {
          if (orderIsSkipped(order)) return;
          eligibleLegs(order).forEach((leg) => {
            if (next[leg.__key]) return;
            if (leg.stop_tenderable === false) return; // non-pending stop: shown disabled, never auto-selected
            const matchesPreselect = !restrictTypes || restrictTypes.includes((leg.leg_type || "").toUpperCase());
            next[leg.__key] = {
              checked: matchesPreselect && !order.non_agent_warehouse,
              agentId: leg.default_agent ? leg.default_agent.agent_id : undefined,
            };
          });
          // linked pair (single-day transfer, no intermediate storage): a
          // preselected leg (e.g. Tender from the Delivery section of Order
          // Details) drags its sibling along — they tender together
          if (order.legs_linked) {
            const pair = eligibleLegs(order).filter((leg) => ["PICKUP", "DELIVERY"].includes(leg.leg_type));
            if (pair.some((leg) => next[leg.__key] && next[leg.__key].checked)) {
              pair.forEach((leg) => {
                next[leg.__key] = { ...next[leg.__key], checked: !order.non_agent_warehouse };
              });
            }
          }
        });
        return next;
      });
    });
  };

  useEffect(() => {
    if (visible) {
      setPendingAdds([]);
      setSearchText("");
      setSearchResults([]);
      setEmailStage(null);
      const orderIds = (selectedOrders || [])
        .map((order) => order.id || order.order_id)
        .filter((id) => id != null && String(id).trim() !== "");
      if (orderIds.length) {
        loadPreview(orderIds);
      } else {
        // Create Tender from Agent Tender Board opens with no preselected
        // orders — show empty modal; skip get_agent_tender_preview.
        setLoading(false);
        setOrders([]);
        setSelection({});
        setExpandedOrders({});
        setExpandedLinehauls({});
        setAvailableAgents([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const agentOptions = (leg) => {
    const options = (leg && leg.available_agents) || availableAgents;
    return options.map((agent) => (
      <Select.Option key={agent.agent_id} value={agent.agent_id}>
        {agent.agent_name || agent.agent_code}
      </Select.Option>
    ));
  };
  const agentById = (agentId) => availableAgents.find((agent) => agent.agent_id === agentId);

  const activeOrders = orders.filter((order) => !orderIsSkipped(order));

  // ---- bulk derivations ----
  const chipTypes = useMemo(() => {
    const present = new Set();
    activeOrders.forEach((order) => eligibleLegs(order).forEach((leg) => present.add(leg.leg_type)));
    return LEG_TYPE_ORDER.filter((type) => present.has(type));
  }, [orders, selection]); // eslint-disable-line react-hooks/exhaustive-deps

  const legsOfType = (type) =>
    activeOrders.flatMap((order) => eligibleLegs(order).filter((leg) => leg.leg_type === type));
  const hops = useMemo(() => {
    const seen = [];
    legsOfType("LINEHAUL").forEach((leg) => {
      const hop = legHop(leg);
      if (!seen.includes(hop)) seen.push(hop);
    });
    return seen;
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLegs = (legs, updater) =>
    setSelection((prev) => {
      const next = { ...prev };
      legs.forEach((leg) => {
        next[leg.__key] = updater(next[leg.__key] || { checked: false, agentId: undefined }, leg);
      });
      return next;
    });

  // Single-day transfer without intermediate storage (order.legs_linked):
  // the freight never parks, so Pickup and Delivery tender as ONE unit —
  // expand any toggled/edited leg set with its linked sibling.
  const expandLinked = (legs) => {
    const byKey = new Map(legs.map((leg) => [leg.__key, leg]));
    legs.forEach((leg) => {
      const order = activeOrders.find((o) => o.order_id === String(leg.__key).split("|")[0]);
      if (!order || !order.legs_linked) return;
      eligibleLegs(order).forEach((sibling) => {
        if (["PICKUP", "DELIVERY"].includes(sibling.leg_type)) byKey.set(sibling.__key, sibling);
      });
    });
    return [...byKey.values()];
  };

  const toggleGroup = (rawLegs) => {
    const legs = expandLinked(rawLegs);
    const shouldCheck = triState(legs, selection) !== "all";
    setLegs(legs, (entry, leg) => ({
      checked: shouldCheck,
      agentId: entry.agentId || (leg.default_agent ? leg.default_agent.agent_id : undefined),
    }));
  };

  const applyAgentToChecked = (legs, agentId) =>
    setLegs(legs, (entry) => (entry.checked ? { ...entry, agentId } : entry));

  const orderAgentSummary = (order) => {
    const checked = eligibleLegs(order).filter((leg) => selection[leg.__key] && selection[leg.__key].checked);
    if (!checked.length) return null;
    const agentIds = _.uniq(checked.map((leg) => selection[leg.__key].agentId));
    if (agentIds.length > 1) return { label: "Multiple Agents", multiple: true };
    const agent = agentById(agentIds[0]);
    return { label: agent ? agent.agent_name || agent.agent_code : "Select Agent", agentId: agentIds[0] };
  };

  // ---- submit ----
  const buildPayload = () => {
    const payloadOrders = [];
    let missingAgent = false;
    activeOrders.forEach((order) => {
      const legs = eligibleLegs(order)
        .filter((leg) => leg.stop_tenderable !== false && selection[leg.__key] && selection[leg.__key].checked)
        .map((leg) => {
          if (!selection[leg.__key].agentId) missingAgent = true;
          return { leg_id: leg.leg_id, agent_id: selection[leg.__key].agentId };
        });
      if (legs.length) payloadOrders.push({ order_id: order.order_id, legs });
    });
    return { payloadOrders, missingAgent };
  };
  const tenderableOrderCount = buildPayload().payloadOrders.length;

  const submit = (openEmailReview) => {
    const { payloadOrders, missingAgent } = buildPayload();
    if (!payloadOrders.length) return;
    if (missingAgent) {
      message.error("Select an agent for every checked leg.");
      return;
    }
    setSubmitting(true);
    saveAgentTender(orgId, payloadOrders, { parkDraft: !openEmailReview }).then((result) => {
      setSubmitting(false);
      if (!result.success) {
        renderAlertMessage(result.errors);
        return;
      }
      if (openEmailReview) {
        setEmailStage({ mode: "tender", batchId: result.batch_id, agentEmails: result.agent_emails || [] });
      } else {
        message.success(I18n.t("agent_tenders.messages.draft_saved"));
        onClose();
        // saving a draft should clear the orders-grid selection exactly like
        // tendering does (UAT feedback #1)
        onTendered();
      }
    });
  };

  // Reaching the email review persists a SAVED draft (needed to build the
  // review). If the user backs out or closes WITHOUT sending, discard that
  // unsent draft so an abandoned preview doesn't leave a card in Drafts.
  // On a successful send, onDone clears emailStage first, so this never fires.
  const discardPendingDraft = () => {
    if (emailStage && emailStage.mode === "tender" && emailStage.batchId) {
      discardAgentTenderDraft(orgId, emailStage.batchId); // fire-and-forget
    }
  };
  const handleClose = () => {
    discardPendingDraft();
    onClose();
  };

  // ---- remove already-tendered leg or whole order from modal list (UI only) ----
  const removeAlreadyTenderedLeg = (order, leg) => {
    setOrders((prev) =>
      prev
        .map((o) => {
          if (o.order_id !== order.order_id) return o;
          return {
            ...o,
            legs: (o.legs || []).filter((l) => l.leg_id !== leg.leg_id),
          };
        })
        .filter((o) => (o.legs || []).length > 0)
    );
    setSelection((prev) => {
      const next = { ...prev };
      delete next[leg.__key];
      return next;
    });
  };

  const removeOrderFromList = (order) => {
    setOrders((prev) => prev.filter((o) => o.order_id !== order.order_id));
    setSelection((prev) => {
      const next = { ...prev };
      (order.legs || []).forEach((leg) => {
        delete next[leg.__key];
      });
      return next;
    });
  };

  // ---- search-to-add: dedicated lightweight lookup (indexed number-prefix
  // match). The previous orders-listing pipeline (stats + full row
  // formatting) made every keystroke take seconds — QA feedback.
  // Stable debounced fn (useMemo) — recreating it every render gave each keystroke its
  // own timer, so the search was never actually debounced. searchSeqRef drops stale
  // responses so a slow older query can't overwrite newer results.
  const searchSeqRef = useRef(0);
  const runSearch = useMemo(
    () =>
      _.debounce((term) => {
        const query = (term || "").toString().trim();
        if (!query || query.length < 3) {
          setSearchResults([]);
          return;
        }
        const seq = ++searchSeqRef.current;
        searchTenderOrders(orgId, query.toUpperCase()).then((result) => {
          if (seq !== searchSeqRef.current) return; // superseded by a newer keystroke
          if (!result.success) {
            setSearchResults([]);
            return;
          }
          const known = new Set([
            ...ordersRef.current.map((order) => String(order.order_id)),
            ...pendingAddsRef.current.map((row) => String(row.id)),
          ]);
          const rows = (result.orders || [])
            .map((row) => ({
              id: row.id != null ? String(row.id) : "",
              number: row.customer_order_number || "",
              account: row.account_name || "",
              type_of_order: row.type_of_order || "",
              order_label: row.order_label || "",
              related_order: "",
            }))
            .filter((row) => row.id && row.number && !known.has(row.id));
          setSearchResults(rows.slice(0, 8));
        });
      }, 350),
    [orgId]
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const stageOrderFromSearch = (row) => {
    setPendingAdds((prev) => {
      if (prev.some((item) => String(item.id) === String(row.id))) return prev;
      return [...prev, row];
    });
    setSearchResults((prev) => prev.filter((item) => String(item.id) !== String(row.id)));
    // warn IMMEDIATELY when the picked order already sits on a tender (UAT
    // feedback #6 — previously you only found out after adding it)
    fetchOrderTenderSummary(orgId, row.id).then((result) => {
      const legs = (result.success && result.legs) || [];
      if (!legs.length) return;
      const tnds = _.uniq(legs.map((leg) => leg.tender_number).filter(Boolean));
      notification.warning({
        message: I18n.t("agent_tenders.messages.already_tendered_warn", {
          order: row.number || "",
        }),
        description: tnds.length ? tnds.join(", ") : undefined,
        duration: 8,
      });
    });
  };

  const removePendingAdd = (orderId) => {
    setPendingAdds((prev) => prev.filter((item) => String(item.id) !== String(orderId)));
  };

  const commitPendingAdds = () => {
    if (!pendingAdds.length) return;
    const ids = pendingAdds.map((row) => row.id);
    setPendingAdds([]);
    setSearchText("");
    setSearchResults([]);
    loadPreview(ids, { append: true });
  };

  // ---- renders ----
  const ChipCheck = () => (
    <span className="atm-chip-check" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );

  const renderChip = (label, state, onClick, key) => (
    <button
      key={key || label}
      type="button"
      className={`atm-chip ${state === "all" ? "atm-chip--all" : state === "some" ? "atm-chip--some" : ""}`}
      onClick={onClick}
      data-testid={`tender-chip-${key || label}`}
    >
      {state === "all" && <ChipCheck />}
      {label}
    </button>
  );

  const renderBulkBox = () => {
    if (activeOrders.length < 2) return null;
    const allLegs = activeOrders.flatMap((order) => eligibleLegs(order));
    return (
      <div className="atm-bulk-box" data-testid="tender-bulk-box">
        <div className="atm-field-group">
          <label className="atm-field-label">Agent Tendering To</label>
          <Select
            size="small"
            style={{ width: "100%" }}
            dropdownMatchSelectWidth={false}
            dropdownClassName="atm-agent-dropdown"
            placeholder="Select agent…"
            value={undefined}
            showSearch
            optionFilterProp="children"
            onSelect={(agentId) => applyAgentToChecked(allLegs, agentId)}
            data-testid="tender-agent-all"
          >
            {agentOptions()}
          </Select>
        </div>
        <div className="atm-field-group">
          <label className="atm-field-label">Leg Types to Tender</label>
          <div className="atm-chip-row">
            {chipTypes.map((type) =>
              renderChip(LEG_TYPE_LABELS[type], triState(legsOfType(type), selection), () => toggleGroup(legsOfType(type)), type)
            )}
          </div>
        </div>
        {hops.length > 0 && (
          <div className="atm-field-group">
            <label className="atm-field-label">Linehaul Segments</label>
            <div className="atm-chip-row">
              {hops.map((hop) => {
                const legs = legsOfType("LINEHAUL").filter((leg) => legHop(leg) === hop);
                return renderChip(hop, triState(legs, selection), () => toggleGroup(legs), hop);
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLegAgent = (leg) => {
    const entry = selection[leg.__key];
    if (!entry || !entry.checked) return null;
    // No agent resolved (e.g. warehouse is is_agent but has no fleet_agent_id):
    // show the dropdown directly with no default instead of the pencil text.
    if (!entry.agentId || editingLeg === leg.__key) {
      return (
        <Select
          size="small"
          style={{ width: 130 }}
          dropdownMatchSelectWidth={false}
          dropdownClassName="atm-agent-dropdown"
          placeholder="Select Agent"
          value={entry.agentId}
          showSearch
          optionFilterProp="children"
          autoFocus={editingLeg === leg.__key}
          onSelect={(agentId) => {
            // linked pair: agent choice mirrors to the sibling leg
            setLegs(expandLinked([leg]), (current) => ({ ...current, agentId }));
            setEditingLeg(null);
          }}
          onBlur={() => setEditingLeg(null)}
        >
          {agentOptions(leg)}
        </Select>
      );
    }
    const agent = agentById(entry.agentId);
    return (
      <button
        type="button"
        className="atm-leg-agent-btn"
        title="Click to change the agent for just this leg"
        onClick={() => setEditingLeg(leg.__key)}
      >
        <span className="atm-leg-agent-btn-label">
          {agent ? agent.agent_name || agent.agent_code : "Select agent"}
        </span>
        <Icon type="edit" />
      </button>
    );
  };

  const renderLegRow = (order, leg, nested = false, { hideDelete = false } = {}) => {
    if (leg.already_tendered) {
      const tenderedTo = leg.tendered_to || {};
      const agentName = tenderedTo.agent_name || tenderedTo.agent_code || "Agent";
      return (
        <div className="atm-inert-row atm-inert-row--already-tendered" key={leg.__key}>
          <Icon type="warning" theme="filled" style={{ color: "#e8862b", fontSize: 16 }} />
          <span className="atm-leg-badge atm-leg-badge--muted">{LEG_SHORT[leg.leg_type] || leg.leg_type}</span>
          <span className="atm-leg-label atm-leg-label--muted">{legLabel(leg, order)}</span>
          <span className="atm-already-tendered">Already Tendered to: {agentName}</span>
          {!hideDelete && (
            <React.Fragment>
              <span className="atm-leg-spacer" />
              <span
                className="atm-inert-delete"
                title="Remove from list"
                data-testid={`tender-remove-${order.order_number}-${leg.leg_id}`}
              >
                <DeleteIcon
                  handleClick={() => removeAlreadyTenderedLeg(order, leg)}
                  color="#FF4D4F"
                  styleObj={{ fontSize: 14 }}
                />
              </span>
            </React.Fragment>
          )}
        </div>
      );
    }
    if (leg.stop_tenderable === false) {
      // Stop has advanced past PENDING (assigned/dispatched/completed) — the leg
      // is already being worked internally, so it can't be tendered. Shown
      // disabled with the reason on hover.
      const reason = leg.not_tenderable_reason || "Only pending stops can be tendered.";
      return (
        <div
          className="atm-inert-row atm-inert-row--not-pending"
          key={leg.__key}
          title={reason}
        >
          <Icon type="info-circle" theme="filled" style={{ color: "#8c8c8c", fontSize: 16 }} />
          <span className="atm-leg-badge atm-leg-badge--muted">{LEG_SHORT[leg.leg_type] || leg.leg_type}</span>
          <span className="atm-leg-label atm-leg-label--muted">{legLabel(leg, order)}</span>
          <span className="atm-already-tendered">
            {leg.stop_state_label ||
              (leg.stop_status ? String(leg.stop_status).toUpperCase() : "Not pending")}
          </span>
        </div>
      );
    }
    const entry = selection[leg.__key] || { checked: false };
    return (
      <div className={`atm-leg-row ${nested ? "atm-leg-row--nested" : ""}`} key={leg.__key}>
        <Checkbox checked={entry.checked} onChange={() => toggleGroup([leg])} />
        <span className={`atm-leg-badge ${entry.checked ? "atm-leg-badge--selected" : ""}`}>
          {LEG_SHORT[leg.leg_type] || leg.leg_type}
        </span>
        <span className="atm-leg-label" onClick={() => toggleGroup([leg])}>{legLabel(leg, order)}</span>
        <span className="atm-leg-spacer" />
        {renderLegAgent(leg)}
      </div>
    );
  };

  const renderLinehaulRows = (order) => {
    const lhLegs = eligibleLegs(order).filter((leg) => leg.leg_type === "LINEHAUL");
    if (!lhLegs.length) return null;
    if (lhLegs.length === 1) return renderLegRow(order, lhLegs[0]);
    const expanded = expandedLinehauls[order.order_id];
    const state = triState(lhLegs, selection);
    const checkedLh = lhLegs.filter((leg) => selection[leg.__key] && selection[leg.__key].checked);
    const agentIds = _.uniq(checkedLh.map((leg) => selection[leg.__key].agentId).filter(Boolean));
    const summaryAgent =
      agentIds.length === 1 ? agentById(agentIds[0]) : null;
    const summaryLabel =
      agentIds.length === 1
        ? (summaryAgent && (summaryAgent.agent_name || summaryAgent.agent_code)) || "Select agent"
        : agentIds.length > 1
          ? "Multiple Agents"
          : null;
    return (
      <div key={`lh-${order.order_id}`}>
        <div className="atm-leg-row">
          <Checkbox checked={state === "all"} indeterminate={state === "some"} onChange={() => toggleGroup(lhLegs)} />
          <span className={`atm-leg-badge ${state !== "none" ? "atm-leg-badge--selected" : ""}`}>L</span>
          <span className="atm-leg-label" onClick={() => toggleGroup(lhLegs)}>
            {whCode(lhLegs[0].origin)}–{whCode(lhLegs[lhLegs.length - 1].destination)}
          </span>
          <span
            className="atm-stops-link"
            onClick={() => setExpandedLinehauls((prev) => ({ ...prev, [order.order_id]: !expanded }))}
          >
            {expanded ? `Hide ${lhLegs.length} Stops` : `Show ${lhLegs.length} Stops`}
          </span>
          <span className="atm-leg-spacer" />
          {summaryLabel && state !== "none" && (
            editingLeg === `lh:${order.order_id}` ? (
              <Select
                size="small"
                style={{ width: 130 }}
                dropdownMatchSelectWidth={false}
                dropdownClassName="atm-agent-dropdown"
                placeholder="Select Agent"
                value={agentIds.length === 1 ? agentIds[0] : undefined}
                showSearch
                optionFilterProp="children"
                autoFocus
                onSelect={(agentId) => {
                  applyAgentToChecked(lhLegs, agentId);
                  setEditingLeg(null);
                }}
                onBlur={() => setEditingLeg(null)}
              >
                {agentOptions()}
              </Select>
            ) : agentIds.length === 1 ? (
              <button
                type="button"
                className="atm-leg-agent-btn"
                title="Click to change agent for these linehaul segments"
                onClick={() => setEditingLeg(`lh:${order.order_id}`)}
              >
                <span className="atm-leg-agent-btn-label">{summaryLabel}</span>
                <Icon type="edit" />
              </button>
            ) : (
              <span className="atm-leg-agent-btn" style={{ cursor: "default" }}>{summaryLabel}</span>
            )
          )}
        </div>
        {expanded && (
          <div className="atm-nested-stops">
            {lhLegs.map((leg) => renderLegRow(order, leg, true))}
          </div>
        )}
      </div>
    );
  };

  const gridRowByOrderId = useMemo(() => {
    const map = {};
    selectedOrders.forEach((row) => {
      map[row.id || row.order_id] = row;
    });
    return map;
  }, [selectedOrders]);

  const renderMoveTypeBadge = (order) => {
    // Reuse the grid's colored badge when we have the grid row (or enough
    // preview fields); fall back to the plain pill for search-added orders.
    const row = gridRowByOrderId[order.order_id] ||
      (order.type_of_order ? { type_of_order: order.type_of_order, order_label: order.order_label } : null);
    if (row) {
      return (
        <span className="atm-move-badge">
          <TypeOfOrder
            order={row}
            orderTypeKey="type_of_order"
            relatedOrderKey="related_order"
            showBadge
            showTooltip={false}
          />
        </span>
      );
    }
    return <span className="atm-move-pill">{order.move_type}</span>;
  };

  const renderOrderCard = (order) => {
    const hasAgentWh = (order.legs || []).some((leg) => leg.agent_warehouse);
    const originCode = whCode(order.origin);
    const destCode = whCode(order.destination);
    const routeLine = (
      <div className="atm-route-line">
        <span>
          {originCode}
          {destCode && destCode !== originCode && (
            <span>
              <span className="atm-dash">–</span>
              {destCode}
            </span>
          )}
          {hasAgentWh && <span style={{ color: "#586fcc", marginLeft: 4 }}>◆</span>}
        </span>
        {order.account_name && <span className="atm-account">{order.account_name}</span>}
      </div>
    );
    if (orderIsSkipped(order)) {
      // Two ways an order arrives with no tenderable legs: everything is done
      // (green check) vs. no agent warehouse anywhere on its route (amber).
      const noAgentWh = !!order.non_agent_warehouse;
      const reason = noAgentWh
        ? I18n.t("agent_tenders.messages.skipped_no_agent_wh")
        : I18n.t("agent_tenders.messages.skipped_complete");
      return (
        <div className="atm-card atm-card--skipped" key={order.order_id} data-testid={`tender-card-${order.order_number}`}>
          <div className="atm-skipped-main">
            <div className="atm-card-title-row">
              {renderMoveTypeBadge(order)}
              <span className="atm-order-no">{order.order_number}</span>
            </div>
            {routeLine}
          </div>
          <span className="atm-skipped-note" style={noAgentWh ? { color: "#9a6700" } : undefined}>
            {reason}
            {noAgentWh ? (
              <Icon type="exclamation-circle" theme="filled" style={{ color: "#9a6700", fontSize: 18 }} />
            ) : (
              <Icon type="check-circle" theme="filled" style={{ color: "#047857", fontSize: 18 }} />
            )}
          </span>
        </div>
      );
    }
    const legs = eligibleLegs(order);
    const fullyTendered = legs.length === 0;
    const state = triState(legs, selection);
    const expanded = expandedOrders[order.order_id];
    const summary = orderAgentSummary(order);
    const phaseTypes = LEG_TYPE_ORDER.filter((type) => legs.some((leg) => leg.leg_type === type));
    const nonLinehaul = (type) => legs.filter((leg) => leg.leg_type === type);
    return (
      <div
        className={[
          "atm-card",
          expanded && "atm-card--expanded",
          fullyTendered && "atm-card--fully-tendered",
        ].filter(Boolean).join(" ")}
        key={order.order_id}
        data-testid={`tender-card-${order.order_number}`}
      >
        <div className="atm-card-head">
          {!fullyTendered && (
            <Checkbox
              checked={state === "all"}
              indeterminate={state === "some"}
              onChange={() => toggleGroup(legs)}
            />
          )}
          <span
            className="atm-card-chevron"
            onClick={() => setExpandedOrders((prev) => ({ ...prev, [order.order_id]: !expanded }))}
            data-testid={`tender-expand-${order.order_number}`}
          >
            <Icon type={expanded ? "down" : "right"} />
          </span>
          <div className="atm-card-info">
            <div className="atm-card-title-row">
              {renderMoveTypeBadge(order)}
              <span className="atm-order-no">{order.order_number}</span>
              {order.non_agent_warehouse && !fullyTendered && (
                <span className="atm-naw-note">
                  <Icon type="exclamation-circle" theme="filled" style={{ fontSize: 13 }} />
                  {I18n.t("agent_tenders.messages.no_agent_wh_note")}
                </span>
              )}
            </div>
            {routeLine}
          </div>
          <div className="atm-phase-chips">
            {!fullyTendered && phaseTypes.map((type) => {
              const typeLegs = nonLinehaul(type);
              const typeState = triState(typeLegs, selection);
              return (
                <button
                  key={type}
                  type="button"
                  className={`atm-phase-chip ${typeState === "all" ? "atm-phase-chip--all" : typeState === "some" ? "atm-phase-chip--some" : ""}`}
                  title={`Toggle ${LEG_TYPE_LABELS[type]} for this order`}
                  onClick={() => toggleGroup(typeLegs)}
                >
                  {LEG_SHORT[type]}
                </button>
              );
            })}
          </div>
          {!fullyTendered && (
            <Select
              size="small"
              style={{ width: 200, flexShrink: 0 }}
              dropdownMatchSelectWidth={false}
              dropdownClassName="atm-agent-dropdown"
              value={summary && !summary.multiple ? summary.agentId : undefined}
              placeholder={summary && summary.multiple ? "Multiple Agents" : "Select Agent"}
              showSearch
              optionFilterProp="children"
              onSelect={(agentId) =>
                summary
                  ? applyAgentToChecked(legs, agentId)
                  : setLegs(legs, () => ({ checked: true, agentId }))
              }
              data-testid={`tender-agent-${order.order_number}`}
            >
              {agentOptions()}
            </Select>
          )}
          {fullyTendered && (
            <span
              className="atm-order-delete"
              title="Remove from list"
              data-testid={`tender-remove-order-${order.order_number}`}
            >
              <DeleteIcon
                handleClick={() => removeOrderFromList(order)}
                color="#FF4D4F"
                styleObj={{ fontSize: 14 }}
              />
            </span>
          )}
        </div>
        {expanded && (
          <div className="atm-card-body">
            {!fullyTendered && phaseTypes
              .filter((type) => type !== "LINEHAUL")
              .filter((type) => LEG_TYPE_ORDER.indexOf(type) < LEG_TYPE_ORDER.indexOf("LINEHAUL"))
              .map((type) => nonLinehaul(type).map((leg) => renderLegRow(order, leg)))}
            {!fullyTendered && renderLinehaulRows(order)}
            {!fullyTendered && phaseTypes
              .filter((type) => type !== "LINEHAUL")
              .filter((type) => LEG_TYPE_ORDER.indexOf(type) > LEG_TYPE_ORDER.indexOf("LINEHAUL"))
              .map((type) => nonLinehaul(type).map((leg) => renderLegRow(order, leg)))}
            {order.legs
              .filter((leg) => leg.already_tendered)
              .map((leg) => renderLegRow(order, leg, false, { hideDelete: fullyTendered }))}
          </div>
        )}
      </div>
    );
  };

  const title = (
    <span data-testid="tender-modal-title" className="atm-modal-title-inner">
      <span className="atm-title-icon" aria-hidden="true">
        <TenderIcon color="#1A354D" width="16" height="16" />
      </span>
      <span className="atm-modal-title-text">
        {tenderableOrderCount > 0 ? `Tender ${tenderableOrderCount} Order${tenderableOrderCount > 1 ? "s" : ""} to Agent` : "Tender Orders to Agent"}
      </span>
    </span>
  );

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={handleClose}
      width={720}
      maskClosable={false}
      wrapClassName="lp-v3 atm-modal"
      footer={
        <React.Fragment>
          <Button className="atm-btn-ghost" onClick={handleClose}>
            Cancel
          </Button>
          <span className="atm-footer-spacer" />
          <Button
            disabled={tenderableOrderCount === 0 || submitting}
            onClick={() => submit(false)}
            data-testid="tender-save-draft"
            style={{ marginRight: 8 }}
          >
            Save
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={tenderableOrderCount === 0}
            onClick={() => submit(true)}
            data-testid="tender-submit"
          >
            {tenderableOrderCount > 0 ? `Tender ${tenderableOrderCount} Order${tenderableOrderCount > 1 ? "s" : ""}` : "Tender Orders"}
          </Button>
        </React.Fragment>
      }
    >
      <div className="atm-search">
        {pendingAdds.length > 0 && (
          <div className="atm-pending-adds" data-testid="tender-pending-adds">
            <div className="atm-pending-chips">
              {pendingAdds.map((row) => (
                <span className="atm-pending-chip" key={row.id}>
                  <span className="atm-pending-chip-label">{row.number}</span>
                  <button
                    type="button"
                    className="atm-pending-chip-x"
                    title="Remove"
                    aria-label={`Remove ${row.number}`}
                    onClick={() => removePendingAdd(row.id)}
                    data-testid={`tender-pending-remove-${row.number}`}
                  >
                    <Icon type="close" />
                  </button>
                </span>
              ))}
            </div>
            <Button
              type="primary"
              size="small"
              className="atm-pending-add-btn"
              onClick={commitPendingAdds}
              data-testid="tender-add-to-tenders"
            >
              Add to Tender
            </Button>
          </div>
        )}
        <div className="atm-search-field">
          <Input
            placeholder="Search Order Number to Add"
            prefix={<Icon type="search" style={{ fontSize: 16 }} />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              runSearch(e.target.value);
            }}
            data-testid="tender-order-search"
          />
          {searchResults.filter((row) => !pendingAdds.some((item) => String(item.id) === String(row.id))).length > 0 && (
            <div className="atm-search-results">
              {searchResults
                .filter((row) => !pendingAdds.some((item) => String(item.id) === String(row.id)))
                .map((row) => (
                <div className="atm-search-result" key={row.id} onClick={() => stageOrderFromSearch(row)}>
                  <span className="atm-search-result-icon">
                    <TypeOfOrder
                      order={row}
                      orderTypeKey="type_of_order"
                      relatedOrderKey="related_order"
                      showBadge
                      showTooltip={false}
                    />
                  </span>
                  <span className="atm-search-result-meta">
                    <strong className="atm-search-result-number">{row.number}</strong>
                    {row.account ? <span className="atm-muted">{row.account}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Spin spinning={loading}>
        <div className="atm-body-stack">
          {renderBulkBox()}
          {orders.map((order) => renderOrderCard(order))}
          {!loading && orders.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#61667d" }}>
              <Icon type="codepen" style={{ fontSize: 42, color: "#5a6ef2", display: "block", marginBottom: 10 }} />
              No orders have been selected, add orders to tender them.
            </div>
          )}
        </div>
      </Spin>
      {emailStage && (
        <TenderEmailModal
          key={`${emailStage.mode}-${emailStage.batchId || "revoke"}-${(emailStage.agentEmails || []).map((a) => a.agent_id).join(",")}`}
          visible
          mode={emailStage.mode}
          orgId={orgId}
          batchId={emailStage.batchId}
          agentEmails={emailStage.agentEmails}
          revokeOrders={emailStage.revokeOrders}
          singleOrder={tenderableOrderCount === 1}
          onBack={() => {
            discardPendingDraft();
            setEmailStage(null);
          }}
          onDone={(mode) => {
            setEmailStage(null);
            if (mode === "revoke") {
              loadPreview(orders.map((order) => order.order_id));
            } else {
              onClose();
              onTendered();
            }
          }}
        />
      )}
    </Modal>
  );
};

TenderOrdersModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedOrders: PropTypes.array,
  onTendered: PropTypes.func,
  // When set (e.g. ["PICKUP"] from Order Details Pickup Driver), only those
  // leg types are checked on open. Bulk/create flows leave this unset.
  preselectLegTypes: PropTypes.arrayOf(PropTypes.string),
};

TenderOrdersModal.defaultProps = {
  selectedOrders: [],
  onTendered: () => {},
  preselectLegTypes: null,
};

export default TenderOrdersModal;
